import {QuoteWrapper} from '../models/quote/quote-wrapper';
import {ServiceLine} from '../models/quote/service-line';
import {QuoteService} from '../models/quote/quote-service';
import {PricingItem} from '../models/quote/pricing-item/pricing-item';
import {EnumPricingItemType} from '../models/quote/pricing-item/pricing-item-type';
import { cloneDeep } from 'lodash-es';

import {
    Article as QuoteEngineArticle,
    ConsumablePricingItem as QuoteEngineConsumablePricingItem,
    Corridor as QuoteEngineCorridor,
    DeviceConsumablePricingItem as QuoteEngineDeviceConsumablePricingItem,
    DeviceConsumableWithSuppPricingItem as QuoteEngineDeviceConsumableWithSuppPricingItem,
    DevicePricingItem as QuoteEngineDevicePricingItem,
    OptionPricingItem as QuoteEngineOptionPricingItem,
    ExtraFee as QuoteEngineExtraFee,
    PackPricingItem as QuoteEnginePackPricingItem,
    QuoteService as QuoteEngineQuoteService,
    QuoteWrapper as QuoteEngineQuoteWrapper,
    ServiceLine as QuoteEngineServiceLine,
    CustomGroup as QuoteEngineCustomGroup, CorridorLevel,
} from 'quote-domain-core';
import {EnumCorridorColor} from '../models/enum/enum-corridor-color';
import {Corridor} from '../models/corridor/corridor';
import {DeviceConsumableWithSuppPricingItem} from '../models/quote/pricing-item/device-consumable-with-supp-pricing-item';

export class QuoteEnginePrototypeMapper {

    public static setQuoteWrapperPrototype = (quoteWrapper: QuoteWrapper): QuoteEngineQuoteWrapper => {

        let newQuoteWrapper = Object.setPrototypeOf(cloneDeep(quoteWrapper), QuoteEngineQuoteWrapper.prototype);
        newQuoteWrapper.serviceLines.forEach(serviceLine => {
            QuoteEnginePrototypeMapper.setServiceLinePrototype(serviceLine);
        });

        return newQuoteWrapper as unknown as QuoteEngineQuoteWrapper;
    };

    public static setServiceLinePrototype = (serviceLine: ServiceLine): QuoteEngineServiceLine => {
        let serviceLineQuoteEngine = Object.setPrototypeOf(serviceLine, QuoteEngineServiceLine.prototype);

        if (serviceLine.renewalQuoteService) {
            serviceLineQuoteEngine.renewalQuoteService = QuoteEnginePrototypeMapper.setQuoteServicePrototype(serviceLine.renewalQuoteService);
        }
        if (serviceLine.existingQuoteService) {
            serviceLineQuoteEngine.existingQuoteService =QuoteEnginePrototypeMapper.setQuoteServicePrototype(serviceLine.existingQuoteService);
        }
        if (serviceLine.newQuoteService) {
            serviceLineQuoteEngine.newQuoteService = QuoteEnginePrototypeMapper.setQuoteServicePrototype(serviceLine.newQuoteService);
        }
        return serviceLineQuoteEngine;
    };

    public static setQuoteServicePrototype = (quoteService: QuoteService): QuoteEngineQuoteService => {
        quoteService = cloneDeep(quoteService)
        let quoteEngineQuoteService = Object.setPrototypeOf(quoteService, QuoteEngineQuoteService.prototype);
        quoteEngineQuoteService.extraFees = quoteService.extraFees.map((extraFee, index, extraFees) => {
            return Object.setPrototypeOf(extraFee, QuoteEngineExtraFee.prototype);
        });
        quoteService.customGroups.forEach(customGroup => {
            Object.setPrototypeOf(customGroup, QuoteEngineCustomGroup.prototype);
            customGroup.pricingItems.forEach((pricingItem, index, pricingItems) => {
                pricingItems[index] = Object.setPrototypeOf(pricingItem, PricingItem.prototype);
                switch (pricingItems[index].pricingItemType) {
                    case EnumPricingItemType.CONSUMABLE: {
                        Object.setPrototypeOf(pricingItem, QuoteEngineConsumablePricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineConsumablePricingItem).consumable);
                        break;
                    }
                    case EnumPricingItemType.PACK: {
                        Object.setPrototypeOf(pricingItem, QuoteEnginePackPricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEnginePackPricingItem).device);
                        break;
                    }
                    case EnumPricingItemType.DEVICE: {
                        Object.setPrototypeOf(pricingItem, QuoteEngineDevicePricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineDevicePricingItem).device);
                        break;
                    }
                    case EnumPricingItemType.DEVICE_CONSUMABLE: {
                        Object.setPrototypeOf(pricingItem, QuoteEngineDeviceConsumablePricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineDeviceConsumablePricingItem).device);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineDeviceConsumablePricingItem).consumable);
                        break;
                    }
                    case EnumPricingItemType.DEVICE_CONSUMABLE_WITH_SUPP: {
                        Object.setPrototypeOf(pricingItem, QuoteEngineDeviceConsumableWithSuppPricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineDeviceConsumableWithSuppPricingItem).device);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineDeviceConsumableWithSuppPricingItem).consumable);
                        if ((pricingItem as unknown as QuoteEngineDeviceConsumableWithSuppPricingItem).deviceConsumableCorridor) {
                            (pricingItem as unknown as QuoteEngineDeviceConsumableWithSuppPricingItem).deviceConsumableCorridor = QuoteEnginePrototypeMapper.mapCorridor((pricingItem as DeviceConsumableWithSuppPricingItem).deviceConsumableCorridor);
                        }
                        break;
                    }
                    case EnumPricingItemType.OPTION: {
                        Object.setPrototypeOf(pricingItem, QuoteEngineOptionPricingItem.prototype);
                        QuoteEnginePrototypeMapper.setArticlePrototype((pricingItem as unknown as QuoteEngineOptionPricingItem).option);
                        break;
                    }
                }

            });
        });
        quoteEngineQuoteService.corridorConsumables = QuoteEnginePrototypeMapper.mapCorridor(quoteService.corridorConsumables);
        quoteEngineQuoteService.corridorDevices = QuoteEnginePrototypeMapper.mapCorridor(quoteService.corridorDevices);
        quoteEngineQuoteService.globalCorridor = QuoteEnginePrototypeMapper.mapCorridor(quoteService.globalCorridor);
        quoteEngineQuoteService.globalCorridorOnlyNegociable = QuoteEnginePrototypeMapper.mapCorridor(quoteService.globalCorridorOnlyNegociable);


        return quoteService as unknown as QuoteEngineQuoteService;
    };

    public static setArticlePrototype(article: QuoteEngineArticle): QuoteEngineArticle {
        let typedArticle = Object.setPrototypeOf(article, QuoteEngineArticle.prototype);
        if (typedArticle.corridor) {
            typedArticle.corridor = QuoteEnginePrototypeMapper.mapCorridor(typedArticle.corridor);
        }
        return typedArticle as unknown as QuoteEngineArticle;
    }

    public static mapCorridor(corridor: Corridor): QuoteEngineCorridor {
        if (!!corridor && !!corridor?.p90) {
            const levels = new Map<EnumCorridorColor, CorridorLevel>();
            levels.set(EnumCorridorColor.GREEN, new CorridorLevel(corridor?.pmax ? corridor?.pmax : '10000', corridor?.p90.toString()));
            levels.set(EnumCorridorColor.LIGHTGREEN, new CorridorLevel(corridor?.p90.toString(), corridor?.p50p90.toString()));
            levels.set(EnumCorridorColor.ORANGE, new CorridorLevel(corridor?.p50p90.toString(), corridor?.p50.toString()));
            levels.set(EnumCorridorColor.RED, new CorridorLevel(corridor?.p50.toString(), corridor?.p10p50.toString()));
            levels.set(EnumCorridorColor.BLACK, new CorridorLevel(corridor?.p10p50.toString(), corridor?.p10.toString()));
            levels.set(EnumCorridorColor.BELOW_BLACK, new CorridorLevel(corridor?.p10.toString(), '0'));

            const corridorInstance = Object.assign(new QuoteEngineCorridor(), {
                levels: levels,
                isNeutral: corridor?.isNeutral
            });
            return Object.setPrototypeOf(corridorInstance, QuoteEngineCorridor.prototype);
        }
        return undefined;

    }

}
